import { DeleteOutlined } from '@ant-design/icons';
import { AddPhotoIcon } from '@assets/icons/addPhoto';
import { Button, Card, Drawer, Image, message, Typography, Upload } from 'antd';
import type {
  RcFile,
  UploadChangeParam,
  UploadFile,
} from 'antd/es/upload/interface';
import React, { DragEvent, useMemo, useState } from 'react';
import styled from 'styled-components';

import { AllowedFileTypes } from '../types/programModulesAndContents';
import { TransparentButton } from './ui/TransparentButton';

export interface DrawerFileProps {
  file: RcFile;
  preview: string | null;
  name: string;
}

export interface UploadDrawerProps {
  visible: boolean;
  onClose: () => void;
  onChange?: (fileData: DrawerFileProps) => void;
  uploadMessage?: string;
  contentType?: AllowedFileTypes | string;
}

const { Text } = Typography;

const UploadDrawer: React.FC<UploadDrawerProps> = ({
  visible,
  contentType,
  uploadMessage,
  onClose,
  onChange,
}) => {
  const [file, setFile] = useState<DrawerFileProps | null>(null);

  const accept = useMemo(() => {
    const fileTypeMap: Record<AllowedFileTypes, string> = {
      [AllowedFileTypes.Video]: 'video/*',
      [AllowedFileTypes.Image]: 'image/*',
      [AllowedFileTypes.Audio]: '.mp3,.wav,.aac',
      [AllowedFileTypes.PDF]: '.pdf',
      [AllowedFileTypes.EBook]: '.epub,.mobi,.doc,.docx,.xls,.xlsx,.txt,.pdf',
      [AllowedFileTypes.Link]: '',
      [AllowedFileTypes.Document]: '.doc,.docx,.xls,.xlsx,.txt,.pdf',
      [AllowedFileTypes.CSV]: '.csv',
    };

    return fileTypeMap[contentType as AllowedFileTypes] || '*';
  }, [contentType]);

  const handleValidateFileType = (file: RcFile | File) => {
    const fileType = file.type.split('/')[0];

    const acceptedTypes = accept.split(',');

    const isAccepted =
      accept.includes(fileType) ||
      acceptedTypes.includes(fileType) ||
      acceptedTypes.some(ext =>
        file.name.toLowerCase().trim().endsWith(ext.trim())
      ) ||
      accept === '*';

    if (!isAccepted) {
      message.error(
        `${file.name.toLocaleLowerCase()} - não é um arquivo válido. Tipo não permitido.`
      );
      return false;
    }

    return true;
  };

  const handleUploadChange = (info: UploadChangeParam<UploadFile>) => {
    const file = info.file as RcFile;

    if (!handleValidateFileType(file)) {
      return;
    }

    if (file.type && file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setFile({ file, preview: previewUrl, name: file.name });
    } else if (file.type) {
      setFile({ file, preview: null, name: file.name });
    } else {
      message.error('Tipo de arquivo não reconhecido ou não suportado.');
    }
  };

  const handleDropValidation = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;

    if (droppedFiles.length) {
      const file = droppedFiles[0] as RcFile;

      handleValidateFileType(file);
    }
  };

  const handleRemove = () => {
    setFile(null);
  };

  const handleClose = () => {
    handleRemove();
    onClose();
  };

  const handleSave = () => {
    if (file) {
      onChange?.(file);
      handleRemove();
      onClose();
    }
  };

  return (
    <Drawer
      title='Selecionar arquivo'
      placement='right'
      onClose={handleClose}
      open={visible}
      width={720}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={handleClose} style={{ marginRight: 8 }}>
            Cancelar
          </Button>
          <Button type='primary' disabled={!file} onClick={handleSave}>
            Salvar
          </Button>
        </div>
      }
    >
      {!file ? (
        <UploadDragger
          beforeUpload={() => false}
          onDrop={handleDropValidation}
          onChange={handleUploadChange}
          showUploadList={false}
          accept={accept}
        >
          <div style={{ textAlign: 'center' }}>
            <AddPhotoIcon />
            <p style={{ fontSize: 16 }}>
              <strong style={{ color: '#1668DC' }}>
                Clique para aqui carregar
              </strong>{' '}
              ou arraste e solte
            </p>
            {uploadMessage && <Text type='secondary'>{uploadMessage}</Text>}
          </div>
        </UploadDragger>
      ) : (
        <UploadedFile>
          <FileInfo>
            {file.preview && (
              <Image
                src={file.preview}
                alt={file.name}
                wrapperStyle={{
                  maxHeight: 40,
                  maxWidth: 40,
                  marginRight: '20px',
                }}
                style={{ objectFit: 'contain' }}
                width={40}
                height={40}
              />
            )}
            {file.name}
          </FileInfo>
          <TransparentButton
            icon={<DeleteOutlined />}
            onClick={handleRemove}
            style={{ width: '30px', height: '30px' }}
          />
        </UploadedFile>
      )}
    </Drawer>
  );
};

const UploadedFile = styled(Card).attrs({
  bodyStyle: {
    padding: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  headStyle: { display: 'none' },
})`
  display: flex;
  align-items: center;
  border: 1px dashed #424242;
  background-color: #242424;
  padding: 24px;
  margin-bottom: 16px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const UploadDragger = styled(Upload.Dragger)`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border: dashed 1px #424242;
  border-radius: 4;
`;

export default UploadDrawer;
